import style from "./create-monday-item-menu.module.css";
import { useEffect, useState, useMemo } from "react";
import Konva from "konva";
import {
  useFloating,
  autoUpdate,
  shift,
  flip,
  offset,
  useDismiss,
  useClientPoint,
  useInteractions,
  useTransitionStyles,
  FloatingFocusManager,
} from "@floating-ui/react";
import BoundingBox from "frontend/geometry/bounding-box";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import consts from "shared/consts";
import { MondayBoard } from "shared/integrations/mapper";
import { H1WithMondayLogo, HR, PopupBody, ValidMarker } from "./components";
import { BoardPicker } from "./pickers/board-picker";
import { BoardGroupPicker } from "./pickers/board-group-picker";
import { ItemPicker } from "./pickers/board-subitem-picker";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { SimpleElementsConversion } from "./element-specific/simple-elements";
import { MappedElementsConversion } from "./element-specific/mapped-elements";
import { isElementInGantt } from "frontend/canvas-designer-new/elements/canvas-elements-utils";

export type MondayItemMode = "task-cards" | "gantt-cards";

export interface CreateMondayItemMappingsProps extends Omit<CreateMondayItemProps, "anchor" | "onDismiss"> {
  isMultipleElements: boolean;
  selectedBoard: MondayBoard | undefined;
  selectedGroupId: string;
  selectedItemId: string | undefined;
  onCreatedItems: () => void;
  showInvalids: boolean;
  setShowInvalids: (showInvalids: boolean) => void;
  integrationId: string;
}

export interface CreateMondayItemProps {
  anchor: DOMRectReadOnly;
  nodes: Iterable<Konva.Node>;
  documentId?: string;
  onCreatedItems: () => void;
  onDismiss: () => void;
  isSubitem?: boolean;
}

export function CreateMondayItemMenu(props: CreateMondayItemProps) {
  const { anchor, nodes, documentId, onDismiss, isSubitem = false, onCreatedItems } = props;

  const { refs, floatingStyles, context } = useFloating({
    placement: "left",
    open: true,
    onOpenChange: onDismiss,
    whileElementsMounted: autoUpdate, // autoUpdate is necessary since the popup can change height
    middleware: [
      offset(20),
      flip({ padding: 20 }),
      shift({ crossAxis: true, boundary: document.getElementById("no-controls-area") ?? undefined }),
    ],
  });

  useEffect(() => {
    let bbox: undefined | BoundingBox;
    if (nodes) {
      bbox = BoundingBox.expandOnAll([...nodes].map((n) => n.getClientRect()));
    }
    const realAnchor = bbox ?? anchor;
    refs.setPositionReference({
      getBoundingClientRect() {
        return realAnchor;
      },
    });
  }, []);

  const dismiss = useDismiss(context);
  const clientPoint = useClientPoint(context, { enabled: true, axis: "both", x: anchor.x, y: anchor.y });

  // Merge all the interactions into prop getters
  const { getFloatingProps } = useInteractions([clientPoint, dismiss]);
  const { styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
  });

  return (
    <FloatingFocusManager context={context} returnFocus={true} restoreFocus={true}>
      <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 100 }} {...getFloatingProps()}>
        <div style={styles}>
          <PopupBody>
            <PopupContent documentId={documentId} nodes={nodes} isSubitem={isSubitem} onCreatedItems={onCreatedItems} />
          </PopupBody>
        </div>
      </div>
    </FloatingFocusManager>
  );
}

function PopupContent({
  documentId,
  nodes,
  isSubitem,
  onCreatedItems,
}: {
  documentId?: string;
  nodes: Iterable<Konva.Node>;
  isSubitem: boolean;
  onCreatedItems: () => void;
}) {
  const [selectedBoard, setSelectedBoard] = useState<MondayBoard | undefined>();
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();
  const [showInvalids, setShowInvalids] = useState(false);

  const { mondayIntegration } = useBoardTasks(documentId);

  useEffect(() => {
    if (selectedBoard?.groups.length == 1) {
      setSelectedGroupId(selectedBoard.groups[0].id);
    } else {
      setSelectedGroupId("");
    }
    setSelectedItemId("");
  }, [selectedBoard?.id]);

  const numberOfItems = nodes ? [...nodes].length : 0;
  const isMultipleElements = numberOfItems > 1;

  const memoizedNodes = useDeepEqualMemo(nodes);
  const sectionToRender = useMemo(() => {
    const { hasTaskCards, hasGanttCards } = [...memoizedNodes].reduce(
      (acc, node) => {
        const elementType = node.attrs.type;
        if (elementType === consts.CANVAS_ELEMENTS.TASK_CARD || elementType === consts.CANVAS_ELEMENTS.TASK) {
          if (isElementInGantt({ ...node.attrs.element, type: elementType })) {
            acc.hasGanttCards = true;
          } else {
            acc.hasTaskCards = true;
          }
        }
        return acc;
      },
      { hasTaskCards: false, hasGanttCards: false }
    );

    if (hasTaskCards) return "task-cards";
    if (hasGanttCards) return "gantt-cards";

    return "simple-element";
  }, [memoizedNodes]);

  const sectionProps: CreateMondayItemMappingsProps = {
    documentId,
    nodes,
    isSubitem,
    onCreatedItems,
    selectedBoard,
    selectedGroupId,
    selectedItemId,
    showInvalids,
    setShowInvalids,
    isMultipleElements,
    integrationId: mondayIntegration?.id ?? "",
  };

  return (
    <div className={style.popupContent}>
      <H1WithMondayLogo>
        {isMultipleElements
          ? ` Creating ${numberOfItems} ${isSubitem ? "subitems" : "items"}`
          : ` Create monday ${isSubitem ? "subitem" : "item"}`}
      </H1WithMondayLogo>
      <ValidMarker invalid={showInvalids && !selectedBoard}>
        <BoardPicker
          integrationId={mondayIntegration!.id}
          onBoardSelected={setSelectedBoard}
          selectedBoard={selectedBoard}
          includeSubitems={isSubitem}
          theme={"dark"}
        />
      </ValidMarker>
      <ValidMarker invalid={showInvalids && !!selectedBoard && !(selectedGroupId || selectedItemId)}>
        {isSubitem ? (
          <ItemPicker
            key={selectedBoard?.id || ""}
            integrationId={mondayIntegration!.id}
            boardId={selectedBoard?.id}
            selectedItemId={selectedItemId}
            onItemIdSelected={setSelectedItemId}
            theme="dark"
          />
        ) : (
          <BoardGroupPicker
            key={selectedBoard?.id || ""}
            board={selectedBoard}
            selectedId={selectedGroupId}
            setSelectedId={setSelectedGroupId}
            theme="dark"
          />
        )}
      </ValidMarker>
      <HR />
      {sectionToRender === "task-cards" && <MappedElementsConversion {...sectionProps} mode="task-cards" />}
      {sectionToRender === "gantt-cards" && <MappedElementsConversion {...sectionProps} mode="gantt-cards" />}
      {sectionToRender === "simple-element" && <SimpleElementsConversion {...sectionProps} />}
    </div>
  );
}
